import { Link } from '@tanstack/react-router';
import { Image } from '@unpic/react';
import { useShopifyStore } from '../../context/store-context';
import type { ProductListItem as ProductListItemType } from '../../types/types';
import { formatPrice } from '../../utils/format';
import { percentage } from '../../utils/percentage';
import Badge from '../badge/badge';
import Icon from '../icon';
import styles from './product-list-item.module.scss';

interface ProductListItemProps {
  product: ProductListItemType;
}

export default function ProductListItem({ product }: ProductListItemProps) {
  const {
    state: { favourites },
    toggleProductInFavourites
  } = useShopifyStore();

  const minPrice = product.priceRange.minVariantPrice;
  const compareAt = product.compareAtPriceRange.minVariantPrice;
  const onSale =
    Number(compareAt.amount) > 0 &&
    Number(compareAt.amount) > Number(minPrice.amount);
  const isNewProduct = product.tags.includes('új');
  const isInFavourites = favourites.includes(product.id);

  const getDiscount = () =>
    `-${100 - percentage(Number(minPrice.amount), Number(compareAt.amount))}%`;

  return (
    <div className={styles.productCard}>
      <Link to="/product/$handle" params={{ handle: product.handle }}>
        {product.featuredImage && (
          <Image
            src={product.featuredImage.url}
            layout="fullWidth"
            aspectRatio={1}
            background="var(--bs-tertiary-bg)"
            priority // Eagerly load the featured products images for better performance and SEO
            alt={`${product.title} karkötő`}
          />
        )}
      </Link>
      <div className={styles.productInfo}>
        <Link to="/product/$handle" params={{ handle: product.handle }}>
          <h3 className={styles.productTitle}>{product.title}</h3>
        </Link>
        <h4
          className={`${styles.productPrices} ${
            onSale ? styles.hasCompareAtPrice : ''
          }`}
        >
          <span
            className={styles.productPrice}
            aria-label={onSale ? 'Akciós ár' : 'Ár'}
          >
            {formatPrice(minPrice.amount)}
          </span>
          {onSale && (
            <span
              className={styles.productCompareAtPrice}
              aria-label="Eredeti ár"
            >
              {formatPrice(compareAt.amount)}
            </span>
          )}
        </h4>
      </div>
      <div className={styles.badges}>
        {onSale && <Badge>{getDiscount()}</Badge>}
        {isNewProduct && <Badge backgroundColor="aquamarine">ÚJ</Badge>}
      </div>
      <button
        type="button"
        className={`${styles.favouriteToggler} ${
          isInFavourites ? styles.active : ''
        }`}
        onClick={() => toggleProductInFavourites(product.id)}
        title={
          isInFavourites ? 'Eltávolítás a kedvencekből' : 'Kedvencekhez ad'
        }
      >
        <Icon className={styles.icon} name="heart" size={20} />
      </button>
    </div>
  );
}
